/**
 * SpeedDial.jsx — radial "Pocket-FM style" playback speed dial.
 *
 * Renders a 270° gauge from `min` to `max`, snaps to discrete `steps`
 * (e.g. [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3]).
 * The user drags the knob (or taps a tick) to change speed; the inner
 * digit shows the current value. Optional `Apply` button commits the
 * draft so the audio player only changes once the user releases.
 *
 * Design intent: a single component used by every playback surface in
 * the app (Player.jsx, Reader.jsx TTS, future audiobook drawer) so
 * speed UX is consistent everywhere.
 *
 * Props
 *   value          : number — current committed value
 *   onChange       : (n) => void — fires on every snap (live mode)
 *   onCommit       : (n) => void — fires when user clicks Apply (draft mode)
 *   steps          : number[] — allowed values, ordered ascending
 *   accent         : 'rose'|'indigo'|'amber'|'emerald'|'fuchsia' (default 'rose')
 *   live           : boolean — true = onChange fires on every drag,
 *                     false (default) = collect into draft + Apply button
 *   size           : 'sm'|'md'|'lg' — visual size preset
 *   compact        : boolean — drop "Apply" button + "Playback speed" label,
 *                     useful for embedded contexts
 *   testIdPrefix   : string
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const ACCENTS = {
  rose:    { arc: '#fb7185', arcEnd: '#f43f5e', glow: 'rgba(251,113,133,0.55)',  text: 'text-rose-100',    btn: 'bg-gradient-to-br from-rose-400 to-fuchsia-500 hover:from-rose-300 hover:to-fuchsia-400 text-white' },
  indigo:  { arc: '#818cf8', arcEnd: '#22d3ee', glow: 'rgba(129,140,248,0.55)',  text: 'text-indigo-100',  btn: 'bg-gradient-to-br from-indigo-400 to-cyan-400 hover:from-indigo-300 hover:to-cyan-300 text-white' },
  amber:   { arc: '#fbbf24', arcEnd: '#fb923c', glow: 'rgba(251,191,36,0.55)',   text: 'text-amber-100',   btn: 'bg-gradient-to-br from-amber-300 to-orange-500 hover:from-amber-200 hover:to-orange-400 text-slate-900' },
  emerald: { arc: '#34d399', arcEnd: '#22d3ee', glow: 'rgba(52,211,153,0.55)',   text: 'text-emerald-100', btn: 'bg-gradient-to-br from-emerald-400 to-teal-400 hover:from-emerald-300 hover:to-teal-300 text-slate-900' },
  fuchsia: { arc: '#e879f9', arcEnd: '#a78bfa', glow: 'rgba(232,121,249,0.55)',  text: 'text-fuchsia-100', btn: 'bg-gradient-to-br from-fuchsia-400 to-violet-500 hover:from-fuchsia-300 hover:to-violet-400 text-white' },
};

const SIZES = {
  sm: { box: 180, stroke: 10, knob: 14, font: 28 },
  md: { box: 240, stroke: 14, knob: 18, font: 40 },
  lg: { box: 300, stroke: 16, knob: 22, font: 56 },
};

// Sweep occupies 270°. We start at 135° (south-west) and end at 45° (south-east)
// going clockwise — same arc shape as the Pocket-FM dial.
const SWEEP_START_DEG = 135;
const SWEEP_END_DEG = 45 + 360; // 405° (clockwise sweep of 270°)
const SWEEP_TOTAL_DEG = SWEEP_END_DEG - SWEEP_START_DEG; // 270

const polarToXY = (cx, cy, r, angleDeg) => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const valueToAngle = (val, steps) => {
  if (!steps.length) return SWEEP_START_DEG;
  const idx = steps.indexOf(val);
  const ratio = idx >= 0
    ? idx / (steps.length - 1)
    : (val - steps[0]) / (steps[steps.length - 1] - steps[0]);
  return SWEEP_START_DEG + Math.max(0, Math.min(1, ratio)) * SWEEP_TOTAL_DEG;
};

const angleToValue = (angle, steps) => {
  // Normalise to sweep range
  let a = angle;
  if (a < SWEEP_START_DEG) a += 360;
  const ratio = (a - SWEEP_START_DEG) / SWEEP_TOTAL_DEG;
  const clamped = Math.max(0, Math.min(1, ratio));
  const idx = Math.round(clamped * (steps.length - 1));
  return steps[idx];
};

// Build SVG arc path between two angles (used for both track + filled arc).
const arcPath = (cx, cy, r, startDeg, endDeg) => {
  const start = polarToXY(cx, cy, r, startDeg);
  const end = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
};

const SpeedDial = ({
  value,
  onChange,
  onCommit,
  steps = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3],
  accent = 'rose',
  live = false,
  size = 'md',
  compact = false,
  applyLabel = 'Apply',
  testIdPrefix = 'speeddial',
  className = '',
  formatValue = (v) => `${v}×`,
  formatTick = null,            // optional — falls back to `${s}×`
  subLabel = null,              // override the auto Normal/Slower/Faster pill
  title = 'Playback speed',
}) => {
  const sz = SIZES[size] || SIZES.md;
  const acc = ACCENTS[accent] || ACCENTS.rose;
  const cx = sz.box / 2;
  const cy = sz.box / 2;
  const r = sz.box / 2 - sz.stroke - 4;

  const ref = useRef(null);
  const [draft, setDraft] = useState(value);
  const [dragging, setDragging] = useState(false);

  // External value changes (e.g. preset elsewhere) sync the draft.
  useEffect(() => { setDraft(value); }, [value]);

  const angle = useMemo(() => valueToAngle(draft, steps), [draft, steps]);
  const knobPos = polarToXY(cx, cy, r, angle);

  const startAngle = SWEEP_START_DEG;
  const endAngle = SWEEP_END_DEG;

  // Track + filled arc paths
  const trackD = arcPath(cx, cy, r, startAngle, endAngle);
  const filledD = arcPath(cx, cy, r, startAngle, angle);

  const updateFromPointer = useCallback((clientX, clientY) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = clientX - (rect.left + rect.width / 2);
    const y = clientY - (rect.top + rect.height / 2);
    let deg = (Math.atan2(y, x) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    const next = angleToValue(deg, steps);
    if (next !== draft) {
      setDraft(next);
      if (live && onChange) onChange(next);
    }
  }, [draft, live, onChange, steps]);

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    updateFromPointer(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    updateFromPointer(e.clientX, e.clientY);
  };
  const onPointerUp = () => {
    if (dragging) setDragging(false);
  };

  useEffect(() => {
    if (!dragging) return undefined;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  const handleApply = () => {
    if (onCommit) onCommit(draft);
    else if (onChange) onChange(draft);
  };

  // Tick labels — show only ends + every other inner so we don't crowd at sm size.
  const tickIndices = steps.length <= 7
    ? steps.map((_, i) => i)
    : [0, Math.floor(steps.length * 0.25), Math.floor(steps.length / 2),
       Math.floor(steps.length * 0.75), steps.length - 1];

  return (
    <div
      className={`flex flex-col items-center gap-3 ${className}`}
      data-testid={`${testIdPrefix}-root`}
    >
      {!compact && (
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-bold">
          {title}
        </p>
      )}
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        className="relative touch-none select-none"
        style={{ width: sz.box, height: sz.box }}
        data-testid={`${testIdPrefix}-svg`}
      >
        {/* Background bezel — soft radial vignette + faint inner ring give
            the dial a "tactile metal" feel instead of floating in space. */}
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle at 50% 38%, rgba(255,255,255,0.06) 0%, rgba(15,23,42,0) 55%), radial-gradient(circle at 50% 110%, ${acc.glow} 0%, rgba(15,23,42,0) 60%)`,
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-2 rounded-full border border-white/[0.04]"
          aria-hidden="true"
        />

        {/* Animated halo ring — only pulses when not at default, so users
            get a passive cue that "speed is off baseline" without staring
            at the digit. */}
        {steps.indexOf(draft) !== Math.floor((steps.length - 1) / 2) && (
          <span
            className="pointer-events-none absolute inset-0 rounded-full animate-[ping_2.4s_ease-in-out_infinite] opacity-30"
            style={{ boxShadow: `inset 0 0 0 1px ${acc.arc}` }}
            aria-hidden="true"
          />
        )}

        <svg width={sz.box} height={sz.box} className="block relative">
          <defs>
            <linearGradient id={`${testIdPrefix}-arcgrad`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={acc.arc} />
              <stop offset="100%" stopColor={acc.arcEnd} />
            </linearGradient>
            <radialGradient id={`${testIdPrefix}-knobgrad`} cx="35%" cy="32%" r="80%">
              <stop offset="0%"  stopColor="#ffffff" stopOpacity="0.95" />
              <stop offset="35%" stopColor={acc.arc} stopOpacity="1" />
              <stop offset="100%" stopColor={acc.arcEnd} stopOpacity="1" />
            </radialGradient>
            <filter id={`${testIdPrefix}-knobshadow`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="b" />
              <feOffset in="b" dy="2" result="ob" />
              <feMerge>
                <feMergeNode in="ob" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer faint guide ring */}
          <circle
            cx={cx}
            cy={cy}
            r={r + sz.stroke / 2 + 6}
            stroke="rgba(148,163,184,0.06)"
            strokeWidth={1}
            fill="none"
          />

          {/* Track — dashed for a refined, premium look (vs solid stroke). */}
          <path
            d={trackD}
            stroke="rgba(148,163,184,0.16)"
            strokeWidth={sz.stroke}
            fill="none"
            strokeLinecap="round"
            strokeDasharray="2 6"
          />

          {/* Filled arc — gradient stroke + glow for depth. */}
          <path
            d={filledD}
            stroke={`url(#${testIdPrefix}-arcgrad)`}
            strokeWidth={sz.stroke}
            fill="none"
            strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 10px ${acc.glow})` }}
          />

          {/* Tick marks at every step */}
          {steps.map((s, i) => {
            const a = valueToAngle(s, steps);
            const inner = polarToXY(cx, cy, r - sz.stroke / 2 - 4, a);
            const outer = polarToXY(cx, cy, r + sz.stroke / 2 + 4, a);
            const draftIdx = steps.indexOf(draft);
            const isActive = draftIdx >= 0 ? i <= draftIdx : false;
            return (
              <line
                key={i}
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke={isActive ? acc.arc : 'rgba(148,163,184,0.28)'}
                strokeWidth={i === Math.floor((steps.length - 1) / 2) ? 2.5 : 1.5}
                strokeLinecap="round"
                style={isActive ? { filter: `drop-shadow(0 0 4px ${acc.glow})` } : undefined}
              />
            );
          })}
          {/* Tick labels */}
          {tickIndices.map((i) => {
            const s = steps[i];
            const a = valueToAngle(s, steps);
            const labelPos = polarToXY(cx, cy, r + sz.stroke + 14, a);
            const isCurrent = s === draft;
            return (
              <text
                key={`label-${i}`}
                x={labelPos.x}
                y={labelPos.y + 4}
                textAnchor="middle"
                fontSize={11}
                fill={isCurrent ? acc.arc : 'rgba(148,163,184,0.7)'}
                fontWeight={isCurrent ? 800 : 600}
                style={{
                  pointerEvents: 'none',
                  letterSpacing: '0.04em',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  transition: 'fill 0.18s',
                }}
              >
                {formatTick ? formatTick(s) : `${s}×`}
              </text>
            );
          })}

          {/* Knob — glass orb: gradient fill + soft shadow + highlight ring */}
          <circle
            cx={knobPos.x}
            cy={knobPos.y}
            r={sz.knob + 6}
            fill={acc.arc}
            opacity="0.18"
            style={{ pointerEvents: 'none' }}
          />
          <circle
            cx={knobPos.x}
            cy={knobPos.y}
            r={sz.knob}
            fill={`url(#${testIdPrefix}-knobgrad)`}
            stroke="rgba(255,255,255,0.85)"
            strokeWidth={2}
            filter={`url(#${testIdPrefix}-knobshadow)`}
            style={{
              cursor: dragging ? 'grabbing' : 'grab',
              filter: `drop-shadow(0 4px 12px ${acc.glow})`,
            }}
            className={dragging ? '' : 'transition-all duration-300'}
          />
          {/* Specular highlight for glass effect */}
          <ellipse
            cx={knobPos.x - sz.knob * 0.32}
            cy={knobPos.y - sz.knob * 0.42}
            rx={sz.knob * 0.42}
            ry={sz.knob * 0.26}
            fill="rgba(255,255,255,0.75)"
            style={{ pointerEvents: 'none' }}
          />
          {/* Small dimple opposite the highlight for extra depth */}
          <circle
            cx={knobPos.x + sz.knob * 0.32}
            cy={knobPos.y + sz.knob * 0.38}
            r={sz.knob * 0.14}
            fill="rgba(0,0,0,0.22)"
            style={{ pointerEvents: 'none' }}
          />
        </svg>
        {/* Centre digit — gradient text with floating drop shadow */}
        <div
          className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
          aria-hidden="true"
        >
          <span
            key={draft}
            className={`font-black tabular-nums tracking-tight ${acc.text} animate-[pop_0.22s_ease-out]`}
            style={{
              fontSize: sz.font,
              lineHeight: 1,
              background: `linear-gradient(135deg, #ffffff 0%, ${acc.arc} 60%, ${acc.arcEnd} 100%)`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: `drop-shadow(0 0 16px ${acc.glow}) drop-shadow(0 2px 4px rgba(0,0,0,0.4))`,
            }}
            data-testid={`${testIdPrefix}-current`}
          >
            {formatValue(draft)}
          </span>
          {!compact && (
            <span
              className="text-[10px] uppercase tracking-[0.28em] mt-1.5 font-bold"
              style={{
                color: 'rgba(148,163,184,0.7)',
                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
              }}
            >
              {subLabel !== null
                ? subLabel
                : (draft === steps[Math.floor((steps.length - 1) / 2)] ? 'Normal' : draft < steps[Math.floor((steps.length - 1) / 2)] ? 'Slower' : 'Faster')}
            </span>
          )}
        </div>
      </div>

      {!compact && !live && (
        <button
          type="button"
          onClick={handleApply}
          className={`px-9 py-2.5 rounded-full text-sm font-bold shadow-xl ${acc.btn} transition-all duration-200 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5`}
          style={{ boxShadow: `0 8px 24px -8px ${acc.glow}, 0 4px 12px -2px rgba(0,0,0,0.4)` }}
          data-testid={`${testIdPrefix}-apply`}
        >
          {applyLabel}
        </button>
      )}
    </div>
  );
};

export default SpeedDial;
